import { useCallback, useEffect, useRef, useState } from 'react';
import { HIGHLIGHTS, TEXT_COLORS } from '../../constants/editorStyles';
import { htmlToText, sanitizeHtml, PAGE_BREAK_HTML } from '../../utils/sanitizeHtml';
import { ACCEPT_ATTR, uploadImages } from '../../utils/uploadImage';

/*
 * HTML rich-text editor for blog article bodies.
 *
 * It emits the same semantic HTML the public blog stylesheet is written for
 * (<h2>, <p>, <ul>, <blockquote>, <pre><code>, <strong>, <mark>, <img>), so what
 * is typed here renders identically to hand-authored content.
 *
 * Built on contentEditable + document.execCommand. execCommand is formally
 * deprecated but is the only API every browser implements for this, and the
 * alternative is a third-party editor bundle. Its messy output (nested spans,
 * stray attributes, <div> line wrappers) is normalised by sanitizeHtml on save
 * and before preview, so what gets stored is clean regardless.
 *
 * Three views:
 *   Write   — WYSIWYG
 *   HTML    — the raw source, editable (also how you paste in prepared HTML)
 *   Preview — sanitised output rendered with the real `.article-body` styles,
 *             so this is exactly what a reader will see
 */

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const WORDS_PER_MINUTE = 220;

/*
 * Toolbar buttons must not take focus. Without preventDefault on mousedown the
 * editor's selection is gone by the time onClick fires, so a command like Bold
 * would have nothing to apply itself to.
 */
const keepFocus = (e) => e.preventDefault();

/*
 * Defined at module scope, NOT inside RichTextEditor. Declaring it in the render
 * body creates a new component type on every keystroke, which makes React unmount
 * and remount all ~20 toolbar buttons each time a character is typed.
 */
function ToolButton({ onClick, title, children, active = false, disabled = false }) {
  return (
    <button
      type="button"
      className={`rte-btn ${active ? 'is-active' : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={keepFocus}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function RichTextEditor({
  value = '',
  onChange,
  onInsertImages,
  folder = 'blogs',
  placeholder = 'Write the blog…',
  minHeight = 420,
}) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const savedRange = useRef(null);

  const [view, setView] = useState('write');
  const [popover, setPopover] = useState(null); // 'color' | 'highlight' | 'link' | null
  const [linkUrl, setLinkUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState(null);
  const linkInputRef = useRef(null);

  // Falls back to uploading straight to S3 (compressed first) if the parent
  // doesn't supply its own onInsertImages handler.
  const insertImages = onInsertImages || ((files) => uploadImages(files, folder));

  /*
   * Sync `value` into the contentEditable — sanitised, and only when the change
   * came from outside this component.
   *
   * Both halves matter:
   *
   * SANITISE: this assigns into a LIVE document, so unsanitised HTML executes
   * here. `<img src=x onerror=…>` fires and `<iframe>` loads the moment the
   * markup lands. That is reachable by switching to the HTML view, pasting a
   * payload and switching back — and also by opening an existing post whose
   * stored content was injected through the API directly.
   *
   * ONLY WHEN EXTERNAL: while the author types, onInput emits the innerHTML the
   * DOM already has. Rewriting it on every keystroke would put the caret back at
   * the start of the field on every character, so `lastEmitted` is used to
   * recognise our own echo and skip it.
   */
  const lastEmitted = useRef(null);
  const prevView = useRef(view);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || view !== 'write') {
      prevView.current = view;
      return;
    }
    // The surface is unmounted in the other views, so a view switch always needs
    // a resync even if the value itself has not changed.
    const viewJustChanged = prevView.current !== view;
    prevView.current = view;
    if (!viewJustChanged && value === lastEmitted.current) return;

    const next = sanitizeHtml(value);
    if (el.innerHTML !== next) el.innerHTML = next;
  }, [value, view]);

  const emit = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    lastEmitted.current = html;
    onChange(html);
  }, [onChange]);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  /*
   * Re-select the range saved before focus moved to a toolbar control.
   *
   * The range is validated, not trusted: a saved range goes stale when the
   * surface is re-rendered from `value` (the resync assigns innerHTML, which
   * detaches every node the range pointed at). Calling addRange with a detached
   * range does not throw — the browser silently leaves the caret at the start of
   * the field, which is how an inserted image ends up at the top of the article
   * instead of where the author put the caret. Returning false lets the caller
   * choose a sane position instead.
   */
  const restoreSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) return false;
    el.focus();

    const range = savedRange.current;
    const node = range?.commonAncestorContainer;
    if (!range || !node || !node.isConnected || !el.contains(node)) return false;

    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  }, []);

  /** Caret after the last node in the editor — the fallback insert position. */
  const placeCaretAtEnd = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const range = el.ownerDocument.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  const exec = useCallback(
    (command, arg) => {
      editorRef.current?.focus();
      /*
       * styleWithCSS OFF, deliberately.
       *
       * With it on, Bold emits <span style="font-weight: bold"> — presentational,
       * and it loses the semantic meaning that <strong> carries for screen readers
       * and search engines. With it off we get <b>/<i>, which sanitizeHtml maps to
       * <strong>/<em>. Colours do not rely on execCommand at all (they use the
       * class-based wrapSelection below), so there is no reason to turn it on.
       */
      try {
        document.execCommand('styleWithCSS', false, false);
      } catch (_) {
        /* unsupported — the sanitiser normalises <font>/<span> output either way */
      }
      document.execCommand(command, false, arg);
      emit();
    },
    [emit]
  );

  /**
   * Wrap the selection, preserving any formatting already inside it (cloning the
   * range's contents rather than taking `sel.toString()` keeps nested <strong>,
   * links and so on).
   */
  const wrapSelection = useCallback(
    (openTag, closeTag) => {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount || sel.isCollapsed) {
        setNotice('Select some text first.');
        return;
      }
      const holder = document.createElement('div');
      holder.appendChild(sel.getRangeAt(0).cloneContents());
      document.execCommand('insertHTML', false, `${openTag}${holder.innerHTML}${closeTag}`);
      emit();
    },
    [emit]
  );

  const closePopover = useCallback(() => {
    setPopover(null);
    setLinkUrl('');
  }, []);

  // --- Link ----------------------------------------------------------------
  const openLinkBar = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      setNotice('Select the text you want to link first.');
      return;
    }
    saveSelection();
    setLinkUrl('');
    setPopover('link');
    setTimeout(() => linkInputRef.current?.focus(), 0);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) return closePopover();
    restoreSelection();
    // sanitizeHtml adds target/rel for external links and drops unsafe schemes.
    exec('createLink', url);
    closePopover();
  };

  // --- Page break (multi-page articles) -------------------------------------
  const insertPageBreak = () => {
    if (!restoreSelection()) placeCaretAtEnd();
    document.execCommand('insertHTML', false, PAGE_BREAK_HTML);
    emit();
  };

  // --- Images --------------------------------------------------------------
  const pickImages = () => {
    saveSelection();
    fileInputRef.current?.click();
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // let the same file be chosen again later
    if (!files.length) return;

    setNotice(null);
    setUploading(true);
    try {
      const urls = await insertImages(files);
      const inserted = urls.filter(Boolean);
      if (!inserted.length) {
        setNotice('No images were inserted.');
        return;
      }

      /*
       * Each image is its own block, in the order the author picked them.
       * `.article-body img` is display:block so they stack down the page rather
       * than sitting side by side as inline content would.
       */
      const html = inserted
        .map((url) => `<img src="${escapeHtml(url)}" alt="" loading="lazy" />`)
        .join('');

      /*
       * The toolbar stays visible in the HTML and Preview views, but the
       * contentEditable surface is unmounted there — execCommand would have
       * nothing to write into and the just-uploaded images would be silently
       * lost. Append to the source string instead so the upload is never wasted.
       */
      if (view !== 'write') {
        onChange(`${value}${html}`);
        setNotice(`Added ${inserted.length} image(s) at the end of the source.`);
        return;
      }

      // Fall back to the end of the article when there is no usable caret —
      // the author never focused the surface, or the saved range went stale.
      if (!restoreSelection()) placeCaretAtEnd();

      document.execCommand('insertHTML', false, html);
      // Keep the caret after the images so a second insert continues in order
      // instead of jumping back to the previously saved position.
      saveSelection();
      emit();
      setNotice(`Inserted ${inserted.length} image(s).`);
    } catch (err) {
      setNotice(`Image upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  /*
   * Paste is sanitised at the moment of insertion rather than on save, because
   * pasted Word/Docs/webpage HTML carries megabytes of <style> blocks and
   * nested spans that would otherwise sit in the editor DOM and make typing
   * sluggish. Plain-text paste falls through to the browser default.
   */
  const handlePaste = (e) => {
    const html = e.clipboardData.getData('text/html');
    if (!html) return;
    e.preventDefault();
    const clean = sanitizeHtml(html);
    document.execCommand('insertHTML', false, clean);
    emit();
  };

  /*
   * Live markdown shorthand: typing **text** / __text__ / *text* / _text_
   * converts to real <strong>/<em> the instant the closing marker is typed,
   * instead of leaving literal asterisks/underscores sitting in the article
   * (which is what a plain contentEditable does with them otherwise).
   *
   * Only runs when the caret sits in a plain text node right after a closing
   * marker — cheap regex check against the text immediately before the caret,
   * no scanning of the whole editor on every keystroke.
   *
   * `formatting` guards against re-entrancy: execCommand('insertHTML', …) below
   * fires its own synchronous `input` event, which would otherwise re-enter
   * this same handler before it has returned.
   */
  const formatting = useRef(false);

  const autoFormatInline = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !sel.isCollapsed) return false;
    const node = sel.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE) return false;
    if (!editorRef.current?.contains(node)) return false;

    const offset = sel.anchorOffset;
    const textBefore = node.textContent.slice(0, offset);

    let tag = null;
    let match = /\*\*([^\s*](?:[^*]*[^\s*])?)\*\*$/.exec(textBefore);
    if (match) tag = 'strong';
    if (!match) {
      match = /__([^\s_](?:[^_]*[^\s_])?)__$/.exec(textBefore);
      if (match) tag = 'strong';
    }
    if (!match) {
      match = /(?:^|[^*])\*([^\s*](?:[^*]*[^\s*])?)\*$/.exec(textBefore);
      if (match) tag = 'em';
    }
    if (!match) {
      match = /(?:^|[^_])_([^\s_](?:[^_]*[^\s_])?)_$/.exec(textBefore);
      if (match) tag = 'em';
    }
    if (!match || !tag) return false;

    const whole = match[0].startsWith('*') || match[0].startsWith('_')
      ? match[0]
      : match[0].slice(1); // drop the leading non-marker char captured by the lookaround group
    const matchStart = offset - whole.length;

    const range = document.createRange();
    range.setStart(node, matchStart);
    range.setEnd(node, offset);
    sel.removeAllRanges();
    sel.addRange(range);

    formatting.current = true;
    // Trailing zero-width space so the caret lands outside the new inline tag —
    // without it the browser keeps typing *inside* the <strong>/<em>.
    document.execCommand('insertHTML', false, `<${tag}>${escapeHtml(match[1])}</${tag}>&#8203;`);
    formatting.current = false;
    return true;
  }, []);

  const handleInput = useCallback(() => {
    if (formatting.current) {
      emit();
      return;
    }
    autoFormatInline();
    emit();
  }, [emit, autoFormatInline]);

  // Ctrl/Cmd+B/I/U are handled natively by contentEditable; these are the extras.
  const handleKeyDown = (e) => {
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === 'k') {
      e.preventDefault();
      openLinkBar();
    } else if (key === 'e') {
      e.preventDefault();
      wrapSelection('<code>', '</code>');
    }
  };

  const cleanHtml = sanitizeHtml(value);
  const words = htmlToText(cleanHtml).split(' ').filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / WORDS_PER_MINUTE));

  return (
    <div className="rte">
      <div className="rte-bar">
        {/* Block format */}
        <div className="rte-group">
          <ToolButton title="Paragraph" onClick={() => exec('formatBlock', 'p')}>
            ¶
          </ToolButton>
          <ToolButton title="Heading 2" onClick={() => exec('formatBlock', 'h2')}>
            H2
          </ToolButton>
          <ToolButton title="Heading 3" onClick={() => exec('formatBlock', 'h3')}>
            H3
          </ToolButton>
        </div>

        {/* Inline format */}
        <div className="rte-group">
          <ToolButton title="Bold (Ctrl+B)" onClick={() => exec('bold')}>
            <strong>B</strong>
          </ToolButton>
          <ToolButton title="Italic (Ctrl+I)" onClick={() => exec('italic')}>
            <em>I</em>
          </ToolButton>
          <ToolButton title="Underline (Ctrl+U)" onClick={() => exec('underline')}>
            <span style={{ textDecoration: 'underline' }}>U</span>
          </ToolButton>
          <ToolButton title="Strikethrough" onClick={() => exec('strikeThrough')}>
            <span style={{ textDecoration: 'line-through' }}>S</span>
          </ToolButton>
          <ToolButton
            title="Inline code (Ctrl+E)"
            onClick={() => wrapSelection('<code>', '</code>')}
          >
            {'</>'}
          </ToolButton>
        </div>

        {/* Colour + highlight */}
        <div className="rte-group">
          <div className="rte-pop-wrap">
            <ToolButton
              title="Text colour"
              active={popover === 'color'}
              onClick={() => {
                saveSelection();
                setPopover((p) => (p === 'color' ? null : 'color'));
              }}
            >
              <span className="rte-swatch-icon" style={{ background: '#0f766e' }} />A
            </ToolButton>
            {popover === 'color' && (
              <div className="rte-pop" role="group" aria-label="Text colour">
                {TEXT_COLORS.map((c) => (
                  <button
                    key={c.className}
                    type="button"
                    className="rte-swatch"
                    style={{ background: c.swatch }}
                    title={c.label}
                    aria-label={`${c.label} text`}
                    onMouseDown={keepFocus}
                    onClick={() => {
                      restoreSelection();
                      wrapSelection(`<span class="${c.className}">`, '</span>');
                      closePopover();
                    }}
                  />
                ))}
                <button
                  type="button"
                  className="rte-pop-clear"
                  onMouseDown={keepFocus}
                  onClick={() => {
                    restoreSelection();
                    exec('removeFormat');
                    closePopover();
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div className="rte-pop-wrap">
            <ToolButton
              title="Highlight"
              active={popover === 'highlight'}
              onClick={() => {
                saveSelection();
                setPopover((p) => (p === 'highlight' ? null : 'highlight'));
              }}
            >
              <mark className="rte-mark-icon">H</mark>
            </ToolButton>
            {popover === 'highlight' && (
              <div className="rte-pop" role="group" aria-label="Highlight colour">
                {HIGHLIGHTS.map((h) => (
                  <button
                    key={h.className}
                    type="button"
                    className="rte-swatch"
                    style={{ background: h.swatch }}
                    title={h.label}
                    aria-label={`${h.label} highlight`}
                    onMouseDown={keepFocus}
                    onClick={() => {
                      restoreSelection();
                      wrapSelection(`<mark class="${h.className}">`, '</mark>');
                      closePopover();
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Blocks */}
        <div className="rte-group">
          <ToolButton title="Bulleted list" onClick={() => exec('insertUnorderedList')}>
            • ≡
          </ToolButton>
          <ToolButton title="Numbered list" onClick={() => exec('insertOrderedList')}>
            1. ≡
          </ToolButton>
          <ToolButton
            title="Highlight block (quote)"
            onClick={() => exec('formatBlock', 'blockquote')}
          >
            ❝
          </ToolButton>
          <ToolButton title="Code block" onClick={() => exec('formatBlock', 'pre')}>
            { }
          </ToolButton>
          <ToolButton title="Divider" onClick={() => exec('insertHorizontalRule')}>
            —
          </ToolButton>
        </div>

        {/* Insert */}
        <div className="rte-group">
          <div className="rte-pop-wrap">
            <ToolButton title="Insert link (Ctrl+K)" active={popover === 'link'} onClick={openLinkBar}>
              🔗
            </ToolButton>
            {popover === 'link' && (
              <div className="rte-pop rte-pop-link">
                <input
                  ref={linkInputRef}
                  type="url"
                  value={linkUrl}
                  placeholder="https://example.com"
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyLink();
                    }
                    if (e.key === 'Escape') closePopover();
                  }}
                />
                <button type="button" className="btn btn-sm btn-primary" onClick={applyLink}>
                  Add
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={closePopover}>
                  ✕
                </button>
              </div>
            )}
          </div>

          <ToolButton
            title="Insert images (multiple allowed)"
            disabled={uploading}
            onClick={pickImages}
          >
            {uploading ? '…' : '🖼'}
          </ToolButton>
          {/* Same allowlist the backend enforces, so the file picker cannot
              offer a format that would be rejected after it was chosen. */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            hidden
            onChange={handleFiles}
          />

          <ToolButton title="Insert page break — splits this blog into multiple pages" onClick={insertPageBreak}>
            📄
          </ToolButton>
        </div>

        <div className="rte-group">
          <ToolButton title="Clear formatting" onClick={() => exec('removeFormat')}>
            ⌫
          </ToolButton>
          <ToolButton title="Undo" onClick={() => exec('undo')}>
            ↶
          </ToolButton>
          <ToolButton title="Redo" onClick={() => exec('redo')}>
            ↷
          </ToolButton>
        </div>

        {/* View switch */}
        <div className="rte-group rte-views">
          {[
            { key: 'write', label: 'Write' },
            { key: 'html', label: 'HTML' },
            { key: 'preview', label: 'Preview' },
          ].map((v) => (
            <button
              key={v.key}
              type="button"
              className={`rte-view-btn ${view === v.key ? 'is-active' : ''}`}
              onClick={() => {
                setPopover(null);
                setView(v.key);
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {notice && (
        <p className="rte-notice" role="status">
          {notice}
          <button type="button" className="rte-notice-x" onClick={() => setNotice(null)}>
            ✕
          </button>
        </p>
      )}

      {view === 'write' && (
        <div
          ref={editorRef}
          className="rte-surface article-body"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Blog content"
          data-placeholder={placeholder}
          style={{ minHeight }}
          onInput={handleInput}
          onBlur={emit}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
        />
      )}

      {view === 'html' && (
        <textarea
          className="rte-source"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          style={{ minHeight }}
          aria-label="Blog HTML source"
          placeholder="<h2>Section</h2>&#10;<p>Paragraph…</p>"
        />
      )}

      {view === 'preview' && (
        <div className="rte-preview" style={{ minHeight }}>
          {cleanHtml ? (
            <div
              className="article-body"
              // Sanitised immediately above — and this is the same string that
              // will be saved, so the preview cannot flatter the real output.
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: cleanHtml }}
            />
          ) : (
            <p className="rte-empty">Nothing to preview yet.</p>
          )}
        </div>
      )}

      <div className="rte-foot">
        <span>
          {words} word{words === 1 ? '' : 's'} · ~{minutes} min read
        </span>
        <span className="rte-hint">
          Ctrl+B bold · Ctrl+K link · Ctrl+E code · paste is cleaned automatically
        </span>
      </div>
    </div>
  );
}

export default RichTextEditor;
